/** @jsxImportSource @emotion/react */
import { css, CSSObject } from "@emotion/react";

import { SubmitEvent, useState } from "react";

import { useTranslation } from "react-i18next";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import useResponse from "@/shared/hooks/useResponse";
import RadioGroup from "@mui/material/RadioGroup";

import Badge from "../../../../../components/Badge/Badge";
import Radio from "../../../../../components/Radio/Radio";
import { useAuth } from "../../../../../context/Auth/AuthProvider";
import { useMediaQueries } from "../../../../../shared";
import {
  AUTHENTICATION_TYPES,
  ENABLE_AUTHENTICATION_STEPS
} from "../../../types";
import { BorderFrame } from "../components/BorderFrame";
import { Buttons } from "../components/Buttons";
import { InnerBottom } from "../components/InnerBottom";
import { InnerTop } from "../components/InnerTop";
import { useStyles } from "../styles";

interface SelectAuthenticationMethodProps {
  isLoading: boolean;
  type: AUTHENTICATION_TYPES | null;
  changeType: (newType: AUTHENTICATION_TYPES | null) => void;
  changeStep: (newStep: ENABLE_AUTHENTICATION_STEPS) => void;
  getSecretKeySubmit: () => void;
}

const SelectAuthenticationType = ({
  isLoading,
  type,
  changeType,
  changeStep,
  getSecretKeySubmit
}: SelectAuthenticationMethodProps) => {
  const { logoutUser, isLoading: isLogoutLoading } = useAuth();

  const { handleCommonError } = useResponse();

  const { postData } = useAxiosPrivate();

  const { t } = useTranslation();
  const { fromMd } = useMediaQueries();

  const { radioGroup, authRadioLabel, innerBottomSeparator, emailRadioLabel } =
    useStyles();

  const [isSecondaryLoading, setIsSecondaryLoading] = useState(false);

  const handleSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    type === AUTHENTICATION_TYPES.Authenticator
      ? getSecretKeySubmit()
      : changeStep(ENABLE_AUTHENTICATION_STEPS.GetEmailCode);
  };

  const handleBackToSignIn = async () => {
    setIsSecondaryLoading(true);
    try {
      await postData(ApiResources.Logout);
      await logoutUser();
    } catch (error) {
      handleCommonError(error, t);
    }

    setIsSecondaryLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="select-authentication-type">
      <BorderFrame>
        <InnerTop
          title={t("Security##2FA##select authentication type##title")}
          description={t(
            "Security##2FA##select authentication type##description"
          )}
        />

        <InnerBottom>
          <RadioGroup
            data-testid="select-authentication-type-radio-group"
            value={type}
            onChange={e => changeType(e.target.value as AUTHENTICATION_TYPES)}
            sx={radioGroup as CSSObject}
          >
            <Radio
              value={AUTHENTICATION_TYPES.Authenticator}
              label={
                <div css={css(authRadioLabel)}>
                  <span>{t("Security##2FA##title##authentication app")}</span>

                  <Badge color="info">
                    {t(
                      "Security##2FA##select authentication type##recommended"
                    )}
                  </Badge>
                </div>
              }
              info={t(
                "Security##2FA##select authentication type##authentication app description"
              )}
            />

            {fromMd && <div css={css(innerBottomSeparator)} />}

            <Radio
              value={AUTHENTICATION_TYPES.Email}
              label={
                <div css={css(emailRadioLabel)}>
                  {t("Security##2FA##select authentication type##email")}
                </div>
              }
              info={t(
                "Security##2FA##select authentication type##email description"
              )}
            />
          </RadioGroup>
        </InnerBottom>
      </BorderFrame>

      <Buttons
        isWhite
        isLoadingPrimary={isLoading}
        isLoadingSecondary={isSecondaryLoading || isLogoutLoading}
        isPrimaryBtnDisabled={type === null}
        primaryBtnTitle={t("Button##continue")}
        onSecondaryBtnClick={handleBackToSignIn}
        secondaryBtnTitle={t("Button##back to sign in")}
      />
    </form>
  );
};

export { SelectAuthenticationType };
